<template>
  <div
    :id="data.id"
    class="relative flex items-center sm:min-h-[500px] md:py-20 lg:min-h-[640px] 3xl:min-h-[800px]"
    :class="{ 'bg-white': !data.dark }"
  >
    <div class="container flex w-full flex-col md:flex-row">
      <div
        v-if="backgroundImage"
        v-lazy-background="backgroundImage"
        class="-mx-6 aspect-[375/327] max-h-[400px] min-w-full bg-cover bg-center md:absolute md:inset-0 md:mx-0 md:aspect-auto md:max-h-full"
      ></div>

      <div
        class="relative z-10 py-6 text-slate md:ml-[5%] md:mr-auto md:w-[440px] md:p-0 lg:ml-28 3xl:ml-[152px] 3xl:w-[600px]"
        :class="{ 'text-slate md:text-white': data.dark }"
      >
        <UiSectionTitle
          :title="product?.name"
          tag="h1"
          class="title text-3xl uppercase leading-none md:text-5xl xl:text-6xl"
        />

        <div
          v-if="data.description"
          class="mt-4 text-md leading-[1.8rem] xl:text-md"
          v-html="data.description"
        />

        <div
          v-if="similarProducts"
          class="title mt-4 flex flex-wrap gap-4 text-lg uppercase"
        >
          <UiLink
            v-for="(similarProduct, i) in similarProducts"
            :key="i"
            class="flex h-12 w-[69px] items-center justify-center rounded-lg bg-gray-lighten3 text-slate"
            :class="[
              { 'md:bg-white md:bg-opacity-10 md:text-white': data.dark },
              {
                [data.dark
                  ? '!bg-slate !text-white md:!bg-white md:bg-opacity-100 md:!text-slate'
                  : '!bg-slate !text-white']: similarProduct.selected,
              },
            ]"
            :to="similarProduct.link"
          >
            <span v-html="similarProduct.feature_value" />
          </UiLink>
        </div>

        <div
          v-if="data.chips"
          class="title mt-8 flex flex-wrap gap-2 text-xs uppercase leading-none md:mt-10"
        >
          <div
            v-for="(chip, i) in data.chips"
            :key="i"
            class="flex h-6 items-center justify-center whitespace-nowrap rounded-md border-[1px] border-gray px-2 tracking-[0.12em]"
            v-html="chip"
          />
        </div>

        <div
          v-if="
            data.link &&
            Object.keys(data.link).length !== 0 &&
            Object.values(data.link).every((value) => value)
          "
          class="mt-4"
        >
          <UiLink
            :to="data.link.src"
            class="title flex items-center text-sm uppercase leading-none tracking-[0.12em] text-blue"
            :class="data.dark ? 'md:text-white' : 'md:text-slate'"
          >
            <span class="mr-2" v-html="data.link.text" />

            <span class="h-4 w-4">
              <UiIcon icon="arrowRightBold" container-size />
            </span>
          </UiLink>
        </div>

        <UiEnergyLabelLink
          v-if="energyLabelLink && energyLabelSymbol"
          :link-html="energyLabelLink"
          :energy-efficiency-class="energyLabelSymbol"
          height="32"
          class="mt-8"
        />

        <div
          v-if="data.button && data.button.link"
          class="mt-8 flex justify-center sm:justify-start md:mt-10"
        >
          <UiButton
            :min-width="!isBiggerSm ? '327px' : '228px'"
            size="md"
            :to="data.button.link"
            :target="data.button.target"
          >
            <span v-html="data.button.text" />
          </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBreakpoint, useMediaAsset } from '~/composables';
import UiEnergyLabelLink from '~/components/ui/UiEnergyLabelLink.vue';
import { getProductFeatureValueById } from '~/helpers/vue';

interface Props {
  data?: any;
  product?: any;
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({}),
  product: () => ({}),
});

const { getAssetUrl } = useMediaAsset();
const { isBiggerThanBreakpoint: isBiggerSm } = useBreakpoint('sm');

const similarProducts = computed(() =>
  props?.product?.similar_products?.[0].items
    .filter((item: any) => item.active)
    .map((item: any) => ({
      ...item,
      link: `/product/${item.slug}`,
    })),
);

const energyLabelLink = computed(() =>
  getProductFeatureValueById(
    'ADDITIONAL_INFORMATION',
    'EPREL',
    props.product?.feature_groups,
  ),
);

const energyLabelSymbol = computed(() =>
  getProductFeatureValueById(
    'ADDITIONAL_INFORMATION',
    'ENERGY_LABEL',
    props.product?.feature_groups,
  ),
);

const backgroundImage = computed(() =>
  getAssetUrl(
    !isBiggerSm.value && props.data?.backgroundImage?.mobile
      ? props.data?.backgroundImage?.mobile
      : props.data?.backgroundImage?.desktop,
  ),
);
</script>
